// stores/authStore.ts

import api from '@/config/axios';
import { routeAccessService } from '@/services/routeAccessService';
import { rotasDoSync, type RotaDoSync } from '@/utils/rotasDoSync';
import { useTicketBadgeStore } from '@/stores/ticketBadgeStore';
import { useReembolsoBadgeStore } from '@/stores/reembolsoBadgeStore';
import NProgress from 'nprogress';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

// ✅ OTIMIZAÇÃO: Timeouts configuráveis
// Login local (prestadores) usa timeout maior por margem de segurança
// Check-status usa timeout menor porque verifica apenas o banco local
const LOGIN_TIMEOUT = 45000; // 45s para login local
const CHECK_STATUS_TIMEOUT = 15000; // 15s para check-status (banco local é rápido)
const INIT_AUTH_TIMEOUT = 20000; // 20s para inicialização

// Interfaces TypeScript
interface UserPermissions {
    hasAdminAccess: boolean;
    hasEditorAccess: boolean;
    isAdmin: boolean;
    isEditor: boolean;
}

interface User {
    id?: number;
    username: string;
    name: string;
    email: string;
    display_name: string;
    department: string;
    title: string;
    groups: string[];
    isAdminMaster?: boolean;
    isAdmin?: boolean;
    memberOfSiglas?: string[];
    customGroups?: string[];
    papel?: string;
    ativo?: boolean;
    // ✅ NOVO: Permissões de rota vindas do backend
    permissions?: UserPermissions;
    // Dados do diretório (Microsoft Graph / AD)
    dn?: string | null;
    cn?: string | null;
    given_name?: string | null;
    surname?: string | null;
    sAMAccountName?: string | null;
    userPrincipalName?: string | null;
    telephoneNumber?: string | null;
    mobile?: string | null;
    streetAddress?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
    company?: string | null;
    manager?: string | null;
    employeeID?: string | null;
    employeeNumber?: string | null;
    description?: string | null;
    info?: string | null;
    website?: string | null;
    photo_url?: string | null;
    photo?: string | null;
    tipo_auth?: 'local' | 'ldap' | 'azure' | 'external';
    last_login?: string;
    /** Primeiro login do usuário no Hub (nunca sobrescrito) — base do badge "New" do menu. */
    first_login_at?: string | null;
    created_at?: string;
    updated_at?: string;
}

// Interface simplificada para localStorage
interface UserBasic {
    cn: string;
    username: string;
    email: string;
}

export const useAuthStore = defineStore('auth', () => {
    // Estado
    const user = ref<User | null>(null);
    const displayName = computed(() => user.value?.display_name || user.value?.name || 'Usuário');
    const token = ref<string | null>(localStorage.getItem('token'));
    const loading = ref<boolean>(false);
    /** Paths onde o usuário tem acesso via user_route_level (member/editor/admin). Usado no menu para precedência sobre grupos. */
    const userRouteLevelPaths = ref<string[]>([]);
    /** Mapa de permissões: routePath -> accessLevel. Fonte única de verdade para menus e guards. */
    const userAccessMap = ref<Map<string, string>>(new Map());
    /** Flags de papel dinâmico vindas do /me (aprovador de reembolso, gestor de cartão, agente do Desk). */
    const roleFlags = ref<{ isAprovadorReembolso: boolean; isGestorCartao: boolean; isAgenteDesk: boolean }>({
      isAprovadorReembolso: false,
      isGestorCartao: false,
      isAgenteDesk: false,
    });

    // Getters - Computed prontos para uso direto nas páginas
    const isAuthenticated = computed(() => !!token.value && !!user.value);
    const userEmail = computed(() => user.value?.email || '');
    const userGroups = computed(() => user.value?.groups || []);

    // ✅ Verifica se é Admin Master (GLOBAL - da tabela user, campo is_admin = 1)
    // isAdminMaster é geral e aplica-se a todas as rotas
    const isAdminMaster = computed(() => user.value?.isAdminMaster === true);

    // ⚠️ IMPORTANTE: isAdmin e isEditor são ESPECÍFICOS DE ROTA
    // Eles devem ser obtidos via useUserPermissions() para a rota específica que o usuário está acessando
    // NÃO devem ser usados como valores globais do authStore
    //
    // Para verificar isAdmin/isEditor em uma rota específica, use:
    // const { permissions } = useUserPermissions();
    // const isAdmin = permissions.value.isAdmin;
    // const isEditor = permissions.value.isEditor;

    // Actions
    const login = async (username: string, password: string, _isKiosk?: boolean): Promise<{ user: User }> => {
        if (loading.value) return { user: user.value! };

        loading.value = true;

        try {
            // Limpar escape da senha
            let cleanPassword = password;
            if (password.includes('\\\\')) {
                cleanPassword = password.replace(/\\+/g, '\\');
            }

            // ✅ OTIMIZAÇÃO: Timeout configurável para login local de prestadores
            const response = await api.post(
                '/auth/login',
                {
                    username,
                    password: cleanPassword
                },
                {
                    timeout: LOGIN_TIMEOUT
                }
            );

            if (response.data.success) {
                const userData = response.data.user;
                if (!userData.groups || !Array.isArray(userData.groups)) {
                    userData.groups = [];
                }

                // Salvar dados completos no store
                token.value = response.data.token;
                user.value = userData;

                // Garantir que sAMAccountName esteja definido
                if (user.value && !user.value.sAMAccountName && user.value.username) {
                    user.value.sAMAccountName = user.value.username;
                }

                // Salvar apenas dados básicos no localStorage
                const userBasic: UserBasic = {
                    cn: userData.cn || userData.display_name || userData.username,
                    username: userData.username,
                    email: userData.email
                };

                localStorage.setItem('token', response.data.token);
                localStorage.setItem('user', JSON.stringify(userBasic));

                // Sincronizar rotas e carregar permissões em background
                syncFrontendRoutes().catch(() => {});
                fetchUserRouteLevelPaths().catch(() => {});

                // Retornar dados do usuário
                return { user: userData };
            } else {
                throw new Error(response.data.error || 'Erro no login');
            }
        } catch (error: any) {
            console.error('Erro no login:', error);

            // ✅ MELHORADO: Mensagens de erro mais claras e específicas
            // Tratar erros específicos de timeout e rede
            if (error?.isTimeout || error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
                const timeoutError = new Error(
                    'O servidor de autenticação está demorando para responder. Isso pode acontecer em horários de pico. ' +
                        'Por favor, aguarde alguns segundos e tente novamente.'
                );
                (timeoutError as any).isTimeout = true;
                (timeoutError as any).canRetry = true;
                throw timeoutError;
            }

            if (error?.isNetworkError || (!error?.response && error?.request)) {
                const networkError = new Error(
                    'Não foi possível conectar ao servidor. ' + 'Verifique sua conexão com a internet e tente novamente.'
                );
                (networkError as any).isNetworkError = true;
                (networkError as any).canRetry = true;
                throw networkError;
            }

            // Se for erro de autenticação do axios, extrair mensagem mais amigável
            if (error?.response) {
                const status = error.response.status;
                const errorData = error.response.data;

                if (status === 401) {
                    const authError = new Error('Usuário ou senha incorretos. Verifique suas credenciais e tente novamente.');
                    (authError as any).isAuthError = true;
                    (authError as any).canRetry = false; // Não deve tentar automaticamente
                    throw authError;
                } else if (status === 403) {
                    // Usuário inativo ou sem permissão
                    const message = errorData?.message || errorData?.error || 'Usuário inativo ou sem permissão para acessar o sistema.';
                    const forbiddenError = new Error(message);
                    (forbiddenError as any).isForbidden = true;
                    (forbiddenError as any).canRetry = false;
                    throw forbiddenError;
                } else if (status === 503) {
                    // Serviço indisponível (timeout LDAP, servidor offline, etc.)
                    const message =
                        errorData?.message ||
                        'O servidor de autenticação está temporariamente indisponível. ' +
                            'Por favor, tente novamente em alguns instantes.';
                    const serviceError = new Error(message);
                    (serviceError as any).isServiceUnavailable = true;
                    (serviceError as any).canRetry = true;
                    throw serviceError;
                } else if (status === 500) {
                    // Se a resposta incluir uma mensagem específica, usar ela
                    const message =
                        errorData?.message || errorData?.error || 'Erro interno do servidor. Por favor, tente novamente mais tarde.';
                    const serverError = new Error(message);
                    (serverError as any).isServerError = true;
                    (serverError as any).canRetry = true;
                    throw serverError;
                } else if (status >= 400) {
                    // Usar mensagem do servidor se disponível, senão mensagem genérica
                    const message = errorData?.message || errorData?.error || `Erro ${status}. Tente novamente.`;
                    throw new Error(message);
                }
            }

            // Re-throw com mensagem mais amigável se possível
            if (error?.message) {
                throw error;
            }

            throw new Error('Erro desconhecido ao fazer login. Tente novamente.');
        } finally {
            loading.value = false;
        }
    };

    // Flag para evitar múltiplos logouts simultâneos
    let isLoggingOut = false;

    /**
     * Limpa o estado de autenticação SEM redirecionar para login.
     * Útil quando o token está expirado mas estamos em uma rota pública.
     */
    const clearAuthState = (): void => {
        user.value = null;
        token.value = null;
        userRouteLevelPaths.value = [];
        userAccessMap.value = new Map();
        _permissionsPromise = null;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('lastUserCheck');
    };

    // Promise para evitar chamadas concorrentes ao endpoint de permissões
    let _permissionsPromise: Promise<void> | null = null;

    /**
     * Carrega permissões de todas as rotas para o usuário.
     * Popula userAccessMap (routePath -> accessLevel) e userRouteLevelPaths.
     * Usa deduplicação de promises para evitar chamadas concorrentes.
     * @param force - Se true, recarrega mesmo que já tenha dados
     */
    const fetchUserRouteLevelPaths = async (force = false): Promise<void> => {
        // Se já carregou e não é forçado, retornar
        if (!force && userAccessMap.value.size > 0) return;

        // Se já está carregando, aguardar a promise existente
        if (_permissionsPromise) return _permissionsPromise;

        _permissionsPromise = (async () => {
            if (!token.value) {
                userRouteLevelPaths.value = [];
                userAccessMap.value = new Map();
                return;
            }
            try {
                // Usa /me para auto-consulta (não requer permissão admin)
                const response = await api.get('/user-route-levels/me');
                const data = response.data?.data;
                const routes = data?.routes as Array<{ path: string; accessLevel: string }> | undefined;

                const newMap = new Map<string, string>();
                const newPaths: string[] = [];

                if (Array.isArray(routes)) {
                    for (const r of routes) {
                        if (r.path && r.accessLevel) {
                            newMap.set(r.path, r.accessLevel);
                            if (r.accessLevel !== 'none') {
                                newPaths.push(r.path);
                            }
                        }
                    }
                }

                userAccessMap.value = newMap;
                userRouteLevelPaths.value = newPaths;

                if (data?.flags) {
                  roleFlags.value = {
                    isAprovadorReembolso: Boolean(data.flags.isAprovadorReembolso),
                    isGestorCartao: Boolean(data.flags.isGestorCartao),
                    isAgenteDesk: Boolean(data.flags.isAgenteDesk),
                  };
                }

                // Armazenar custom groups do usuário para verificações de permissão no frontend
                const customGroups = data?.user?.customGroups;
if (user.value && Array.isArray(customGroups)) {
                    user.value.customGroups = customGroups;
                }
            } catch {
                userRouteLevelPaths.value = [];
                userAccessMap.value = new Map();
            } finally {
                _permissionsPromise = null;
            }
        })();

        return _permissionsPromise;
    };

    const logout = (): void => {
        // Evitar múltiplos logouts simultâneos
        if (isLoggingOut) {
            return;
        }

        isLoggingOut = true;

        // Iniciar barra de progresso antes do logout
        NProgress.start();

        try {
            // Para o poller do badge de chamados (singleton) — senão segue batendo
            // GET /desk/tickets/new-count em loop de 401 até o reload da página.
            try { useTicketBadgeStore().stop(); } catch { /* best-effort */ }
            try { useReembolsoBadgeStore().stop(); } catch { /* best-effort */ }

            // Limpar estado
            clearAuthState();

            // Finalizar barra de progresso antes do redirecionamento
            // Usar setTimeout pequeno para garantir que a barra apareça
            setTimeout(() => {
                NProgress.done();
                // Redirecionar para a tela de login
                window.location.href = '/login';
            }, 100);
        } catch (error) {
            // Em caso de erro, finalizar barra e redirecionar
            NProgress.done();
            window.location.href = '/login';
        }
    };

    const initializeAuth = async (): Promise<void> => {
        const savedUser = localStorage.getItem('user');
        const savedToken = localStorage.getItem('token');

        if (savedUser && savedToken) {
            try {
                const parsedUser = JSON.parse(savedUser) as UserBasic;
                token.value = savedToken;

                // Verificar se o token ainda é válido (não expirado)
                const tokenParts = savedToken.split('.');
                if (tokenParts.length === 3) {
                    const payload = JSON.parse(atob(tokenParts[1]));
                    const now = Math.floor(Date.now() / 1000);

                    if (payload.exp && payload.exp > now) {
                        // Token ainda válido, buscar dados completos do usuário
                        try {
                            // ✅ OTIMIZAÇÃO: Timeout reduzido - check-status agora usa banco local (rápido)
                            const response = await api.get('/auth/check-status', {
                                timeout: INIT_AUTH_TIMEOUT
                            });
                            if (response.data.success) {
                                // Se a resposta incluir dados completos do usuário, usar esses dados
                                if (response.data.user) {
                                    user.value = response.data.user as User;
                                    fetchUserRouteLevelPaths().catch(() => {});
                                    // Self-heal: admin master ressincroniza rotas ao abrir o app,
                                    // garantindo que módulos novos apareçam em Permissões sem re-login.
                                    if (user.value?.isAdminMaster) syncFrontendRoutes().catch(() => {});
                                } else {
                                    // Fallback: usar dados do JWT se a resposta não incluir dados completos
                                    user.value = {
                                        id: payload.id,
                                        username: payload.username,
                                        name: payload.displayName || payload.username,
                                        email: payload.email,
                                        display_name: payload.displayName,
                                        department: payload.department,
                                        title: payload.role || 'Não informado',
                                        groups: [],
                                        isAdminMaster: payload.isAdminMaster || false,
                                        ativo: true,
                                        tipo_auth: (payload as any).tipo_auth || 'azure',
                                        sAMAccountName: payload.sAMAccountName || payload.username,
                                        permissions: undefined
                                    };
                                    fetchUserRouteLevelPaths().catch(() => {});
                                }
                                return;
                            }
                        } catch (apiError: any) {
                            // ✅ MELHORADO: Log mais informativo e fallback robusto
                            const errorMessage = apiError?.message || 'Erro desconhecido';
                            const isTimeout = apiError?.isTimeout || apiError?.code === 'ECONNABORTED';
                            const isNetworkError = apiError?.isNetworkError || (!apiError?.response && apiError?.request);

                            if (isTimeout) {
                                console.warn('⏱️ Timeout ao verificar status, usando dados do JWT');
                            } else if (isNetworkError) {
                                console.warn('🌐 Erro de rede ao verificar status, usando dados do JWT');
                            } else {
                                console.warn('⚠️ Erro ao verificar status do usuário, usando dados do JWT:', errorMessage);
                            }

                            // Fallback: usar dados do JWT mesmo se a verificação falhar
                            user.value = {
                                id: payload.id,
                                username: payload.username,
                                name: payload.displayName || payload.username,
                                email: payload.email,
                                display_name: payload.displayName,
                                department: payload.department,
                                title: payload.role || 'Não informado',
                                groups: [],
                                isAdminMaster: payload.isAdminMaster || false,
                                ativo: true,
                                tipo_auth: (payload as any).tipo_auth || 'azure',
                                sAMAccountName: payload.sAMAccountName || payload.username,
                                permissions: undefined
                            };
                            fetchUserRouteLevelPaths().catch(() => {});
                            return;
                        }
                    }
                }

                // Token expirado ou inválido - apenas limpar estado sem redirecionar
                // O redirecionamento para login deve ser feito pelo guard do router
                // APENAS quando a rota requer autenticação
                clearAuthState();
            } catch (error) {
                console.error('Erro ao inicializar autenticação:', error);
                // Apenas limpar estado sem redirecionar (pode estar em rota pública)
                clearAuthState();
            }
        }
    };

    /**
     * Verifica o status do usuário no backend
     * Consulta apenas o banco local; a reconciliação com o diretório ocorre no login/sync.
     */
    const checkUserStatus = async (): Promise<boolean> => {
        try {
            // ✅ OTIMIZAÇÃO: Timeout reduzido - check-status agora usa banco local (rápido)
            const response = await api.get('/auth/check-status', {
                timeout: CHECK_STATUS_TIMEOUT
            });

            if (response.data.success) {
                // ✅ MELHORADO: Atualizar dados do usuário se disponíveis
                if (response.data.user) {
                    user.value = response.data.user as User;
                }
                fetchUserRouteLevelPaths().catch(() => {});
                return true;
            } else {
                return false;
            }
        } catch (error: any) {
            // ✅ MELHORADO: Tratamento de erro mais específico
            const status = error.response?.status;
            const shouldLogout = error.response?.data?.shouldLogout;
            const errorMessage = error.response?.data?.error || error.message;

            // Se a resposta indica que deve fazer logout (usuário inativo, etc.)
            if (shouldLogout) {
                console.warn('🚪 Usuário deve ser deslogado:', errorMessage);
                return false;
            }

            // Se for erro 401 (token inválido/expirado) ou 403 (usuário inativo)
            if (status === 401 || status === 403) {
                console.warn(`🚪 Erro ${status}: ${errorMessage}`);
                return false;
            }

            // Se for erro 404 (usuário não encontrado no banco)
            if (status === 404) {
                console.warn('🚪 Usuário não encontrado no sistema');
                return false;
            }

            // Para erros de rede/timeout, não deslogar automaticamente
            // O usuário pode ter perdido conexão temporariamente
            const isTimeout = error?.isTimeout || error?.code === 'ECONNABORTED';
            const isNetworkError = error?.isNetworkError || (!error?.response && error?.request);

            if (isTimeout) {
                console.warn('⏱️ Timeout ao verificar status, mantendo sessão');
            } else if (isNetworkError) {
                console.warn('🌐 Erro de rede, mantendo sessão');
            } else {
                console.warn('⚠️ Erro ao verificar status, mantendo sessão:', errorMessage);
            }

            // Manter sessão em caso de erro de rede
            return true;
        }
    };

    /**
     * Sincroniza rotas do frontend com o banco de dados.
     * Roda uma vez por sessão para garantir que todas as rotas existam no banco.
     * Apenas admin master dispara a sincronização para evitar chamadas desnecessárias.
     */
    const syncFrontendRoutes = async (): Promise<void> => {
        // Verificar se já sincronizou nesta sessão
        const lastSync = sessionStorage.getItem('lastRouteSync');
        if (lastSync) return;

        // Apenas admin master sincroniza rotas (outros usuários não precisam)
        if (!user.value?.isAdminMaster) return;

        try {
            // Importar rotas dinamicamente para evitar dependência circular
            const { publicRoutes, protectedRoutes, specialRoutes } = await import('@/router/routes');

            // Árvore do router → payload (um nível; netos só com `meta.syncFilhos`, ver rotasDoSync)
            const flatRoutes: RotaDoSync[] = rotasDoSync([publicRoutes, protectedRoutes, specialRoutes]);

            // Apps em domínio próprio (CRM | CS) não estão na árvore do router
            // do Hub — sem isto, o sync desativaria as rotas deles no catálogo
            // global e o guard negaria todo mundo, admin master incluído.
            const { EXTERNAL_APP_ROUTES } = await import('@/router/externalAppRoutes');
            flatRoutes.push(...EXTERNAL_APP_ROUTES);

            // Remover duplicatas
            const uniqueRoutes = flatRoutes.filter((route, index, self) => index === self.findIndex((r) => r.path === route.path));

            await routeAccessService.syncRoutes(uniqueRoutes);
            sessionStorage.setItem('lastRouteSync', Date.now().toString());
        } catch {
            // Sync é best-effort, não bloquear o fluxo
        }
    };

    /**
     * Persiste a sessão a partir de um token recebido via SSO.
     * Decodifica o JWT para um usuário mínimo; a hidratação completa vem do check-status.
     */
    const loginWithToken = (jwtToken: string): void => {
        token.value = jwtToken;
        localStorage.setItem('token', jwtToken);
        try {
            const payload = JSON.parse(atob(jwtToken.split('.')[1]));
            const basicUser = {
                cn: payload.displayName || payload.username,
                username: payload.username,
                email: payload.email
            };
            localStorage.setItem('user', JSON.stringify(basicUser));
            user.value = {
                id: payload.id,
                username: payload.username,
                name: payload.displayName || payload.username,
                email: payload.email,
                display_name: payload.displayName,
                department: payload.department,
                title: payload.role || 'Não informado',
                groups: [],
                isAdminMaster: payload.isAdminMaster || false,
                ativo: true,
                tipo_auth: payload.tipo_auth || 'azure',
                sAMAccountName: payload.sAMAccountName || payload.username,
                permissions: undefined
            } as User;
        } catch {
            /* token malformado: check-status fará o fallback/logout */
        }
        // Login via SSO (Microsoft) também precisa sincronizar as rotas do frontend
        // com o banco — caso contrário rotas novas (ex.: Cartão Corporativo) não
        // aparecem no admin de Permissões/Rotas.
        syncFrontendRoutes().catch(() => {});
    };

    return {
        // Estado
        user,
        displayName,
        token,
        loading,
        userRouteLevelPaths,
        userAccessMap, // Mapa routePath -> accessLevel (fonte única de verdade para menus)
        roleFlags,

        // Getters - Usar diretamente nas páginas, não precisa criar computed adicionais
        isAuthenticated,
        userEmail,
        userGroups,
        isAdminMaster, // ✅ true se for admin master (GERAL - da tabela user)
        // ⚠️ isAdmin e isEditor são ESPECÍFICOS DE ROTA - use useUserPermissions() para obter por rota
        // Exemplo: const { permissions } = useUserPermissions(); const isAdmin = permissions.value.isAdmin;

        // Actions
        login,
        loginWithToken,
        logout,
        initializeAuth,
        checkUserStatus,
        fetchUserRouteLevelPaths,
        syncFrontendRoutes
    };
});
